import { useEffect, useRef, useState } from 'react'
import useAsync from 'react-use/esm/useAsync'
import { Button } from '~/components/common/Button'
import { CurieSelect } from '~/components/common/CurieSelect'
import { SimpleTextarea } from '~/components/common/SimpleTextarea'
import {
  AgentRequest,
  SourceOutputExampleType,
  SourceOutputExamples,
  sourceOutputExampleTypeFromJSON,
  sourceOutputExampleTypeToJSON
} from '~/protos/agents'
import { toCamelCase } from '~/utils/shared'
import { cm } from '~/utils/utils'
import { InfoTooltip } from '../InfoToolTip'
import styles from '../../InputOutputToDWLForm.module.scss'
import { Edi, EdiSchemaData, createEdiSchema } from './Edi'
import { EdiFact, EdifactSchemaData, createEdifactSchema } from './EdiFact'
import { Hl7, Hl7SchemaData, createHl7Schema } from './Hl7'
import { SamplesList } from './SamplesList'
import { InputOutputExamples } from './examples'
import { CommonFormState, InputOutputFormProps } from './types'

export function InputOutputForm({
  onChange,
  collapsible = false,
  inputTypeDefaultValue,
  inputDefaultValue,
  outputDefaultValue,
  handleEditorSourceChange,
  handleEditorOutputChange,
  onError,
  onErrorClear,
  withExamplePrefilled = false,
  vmAware = false,
  initVmArgs = '',
  suggestedVmArgs = '',
  isLoadingSuggestedVmArgs = false,
  initInputOutputNotes = ''
}: InputOutputFormProps) {
  const [sourceDataType, setSourceDataType] = useState<string>(
    inputTypeDefaultValue || sourceOutputExampleTypeToJSON(SourceOutputExampleType.DATA_TYPE_XML)
  )
  const initialInput = withExamplePrefilled
    ? InputOutputExamples[sourceDataType]
    : inputDefaultValue
  const initialOutput = withExamplePrefilled
    ? InputOutputExamples[sourceOutputExampleTypeToJSON(SourceOutputExampleType.DATA_TYPE_JSON)]
    : outputDefaultValue
  const [samples, setSamples] = useState<SourceOutputExamples[]>([
    SourceOutputExamples.fromJSON({
      sourceData: initialInput,
      outputData: initialOutput,
      sourceDataType: sourceOutputExampleTypeFromJSON(inputTypeDefaultValue)
    })
  ])

  // Track previous values to detect meaningful changes
  const prevWithExamplePrefilled = useRef(withExamplePrefilled)
  const prevInputTypeDefaultValue = useRef(inputTypeDefaultValue)

  // Update samples only when withExamplePrefilled or inputTypeDefaultValue actually changes
  useEffect(() => {
    const withExamplePrefilledChanged = prevWithExamplePrefilled.current !== withExamplePrefilled
    const inputTypeChanged = prevInputTypeDefaultValue.current !== inputTypeDefaultValue

    // Only reset samples if these specific props changed
    if (withExamplePrefilledChanged || inputTypeChanged) {
      const newInput = withExamplePrefilled
        ? InputOutputExamples[sourceDataType]
        : inputDefaultValue || ''
      const newOutput = withExamplePrefilled
        ? InputOutputExamples[sourceOutputExampleTypeToJSON(SourceOutputExampleType.DATA_TYPE_JSON)]
        : outputDefaultValue || ''

      setSamples([
        SourceOutputExamples.fromJSON({
          sourceData: newInput,
          outputData: newOutput,
          sourceDataType: sourceOutputExampleTypeFromJSON(inputTypeDefaultValue)
        })
      ])

      prevWithExamplePrefilled.current = withExamplePrefilled
      prevInputTypeDefaultValue.current = inputTypeDefaultValue
    }
  }, [
    withExamplePrefilled,
    inputDefaultValue,
    outputDefaultValue,
    sourceDataType,
    inputTypeDefaultValue
  ])

  // Only use explicit initial VM args, not suggested ones (those are placeholders)
  const [formState, setFormState] = useState<CommonFormState>({
    samples,
    sourceDataType,
    inputOutputNotes: initInputOutputNotes,
    vmArgs: initVmArgs,
    sourceDataValidationError: false,
    outputDataValidationError: false
  })

  // Schema specific state
  const [ediSchemaData, setEdiSchemaData] = useState<EdiSchemaData | null>(null)
  const [edifactSchemaData, setEdifactSchemaData] = useState<EdifactSchemaData | null>(null)
  const [hl7SchemaData, setHl7SchemaData] = useState<Hl7SchemaData | null>(null)

  useEffect(() => {
    setFormState(prev => ({ ...prev, vmArgs: initVmArgs }))
  }, [initVmArgs])

  // Update notes when initial notes change
  useEffect(() => {
    if (initInputOutputNotes) {
      setFormState(prev => ({ ...prev, inputOutputNotes: initInputOutputNotes }))
    }
  }, [initInputOutputNotes])

  useEffect(() => {
    if (formState.sourceDataValidationError || formState.outputDataValidationError) {
      onError?.()
    } else {
      onErrorClear?.()
    }
  }, [
    formState.sourceDataValidationError,
    formState.outputDataValidationError,
    onError,
    onErrorClear
  ])

  useAsync(async () => {
    let text =
      '### Sample Input Data\n' +
      samples[0].sourceData +
      '\n### Sample Output Data\n' +
      samples[0].outputData +
      '\n### Notes\n' +
      formState.inputOutputNotes +
      '\n### VM Args\n' +
      formState.vmArgs

    if (sourceDataType === 'DATA_TYPE_EDI' && ediSchemaData) {
      text += `\n### EDI Transaction Version\n${ediSchemaData.ediTransactionVersion}\n### EDI Transaction Type\n${ediSchemaData.ediTransactionType}`
    }
    if (sourceDataType === 'DATA_TYPE_EDIFACT' && edifactSchemaData) {
      text += `\n### EDIFACT Version\n${edifactSchemaData.ediFactVersion}\n### EDIFACT Message\n${edifactSchemaData.ediFactMessage}`
    }
    if (sourceDataType === 'DATA_TYPE_HL7' && hl7SchemaData) {
      text += `\n### HL7 Version\n${hl7SchemaData.hl7Version}\n### HL7 Message\n${hl7SchemaData.hl7Message}\n### HL7 Segment Terminator\n${hl7SchemaData.hl7SegmentTerminator}\n### HL7 Schema Type\n${hl7SchemaData.hl7SchemaType}`
    }

    const request = AgentRequest.fromPartial({
      command: { commandString: text },
      inputOutputNotes: formState.inputOutputNotes,
      vmArgs: formState.vmArgs,
      sourceOutputExamples: samples.map(example =>
        SourceOutputExamples.fromJSON({
          sourceData: example.sourceData,
          outputData: example.outputData,
          sourceDataType: sourceOutputExampleTypeFromJSON(sourceDataType),
          inputEdiSchemaType:
            sourceDataType === 'DATA_TYPE_EDI' && ediSchemaData
              ? createEdiSchema(ediSchemaData)
              : undefined,
          inputEdiFactSchemaType:
            sourceDataType === 'DATA_TYPE_EDIFACT' && edifactSchemaData
              ? createEdifactSchema(edifactSchemaData)
              : undefined,
          inputHl7SchemaType:
            sourceDataType === 'DATA_TYPE_HL7' && hl7SchemaData
              ? createHl7Schema(hl7SchemaData)
              : undefined
        })
      )
    })
    onChange?.({ request })
  }, [
    samples,
    sourceDataType,
    formState.inputOutputNotes,
    formState.vmArgs,
    ediSchemaData,
    edifactSchemaData,
    hl7SchemaData
  ])

  const items = Object.keys(SourceOutputExampleType)
    .filter(
      type => type.startsWith('DATA_') && !type.endsWith('UNKNOWN') && !type.endsWith('MULTIPART')
    )
    .map(key => {
      const label = toCamelCase(key).split(' ').pop()!.toUpperCase()
      return { label, key: key }
    })

  const handleSamplesChange = (newSamples: SourceOutputExamples[]) => {
    setSamples(newSamples)
    setFormState(prev => ({ ...prev, samples: newSamples }))
  }

  const handleSourceDataValidationError = (hasError: boolean) => {
    setFormState(prev => ({ ...prev, sourceDataValidationError: hasError }))
  }

  const handleOutputDataValidationError = (hasError: boolean) => {
    setFormState(prev => ({ ...prev, outputDataValidationError: hasError }))
  }

  return (
    <div className='input-output-form'>
      <div className='input-type'>
        <div className='label font-semibold text-xs text-default-700 mb-[10px] select-none'>
          Sample Input Format
        </div>
        <CurieSelect
          items={items}
          placeholder='Select input format'
          selectedKeys={[sourceDataType]}
          disabled={withExamplePrefilled}
          onSelectionChange={selectedKeys => {
            if (typeof selectedKeys === 'string' || selectedKeys.size === 0) {
              return
            }
            const format = selectedKeys.values().next().value as string
            setSourceDataType(format)
            setFormState(prev => ({ ...prev, sourceDataType: format }))
          }}
        />
      </div>

      {/* Schema Type Components */}
      <Edi
        sourceDataType={sourceDataType}
        samples={samples}
        onChange={handleSamplesChange}
        onSchemaChange={setEdiSchemaData}
      />
      <EdiFact
        sourceDataType={sourceDataType}
        samples={samples}
        onChange={handleSamplesChange}
        onSchemaChange={setEdifactSchemaData}
      />
      <Hl7
        sourceDataType={sourceDataType}
        samples={samples}
        onChange={handleSamplesChange}
        onSchemaChange={setHl7SchemaData}
      />

      <div>
        <SamplesList
          samples={samples}
          sourceDataType={sourceDataType}
          onSamplesChange={handleSamplesChange}
          handleEditorSourceChange={handleEditorSourceChange}
          handleEditorOutputChange={handleEditorOutputChange}
          onSourceDataValidationError={handleSourceDataValidationError}
          onOutputDataValidationError={handleOutputDataValidationError}
          disabled={withExamplePrefilled}
        />
        <Button
          variant='primary'
          className='mt-0'
          isDisabled={withExamplePrefilled}
          onClick={() =>
            handleSamplesChange([
              ...samples,
              SourceOutputExamples.fromJSON({
                sourceData: '',
                outputData: '',
                sourceDataType: sourceOutputExampleTypeFromJSON(sourceDataType)
              })
            ])
          }
        >
          <span className='select-none'>Add another input output sample</span>
        </Button>
      </div>
      {collapsible ? (
        <details className={cm('hidden details', styles.details)}>
          <summary className='summary'>Advanced</summary>
          <div className='description pl-4'>
            <div className='label font-semibold text-xs text-default-700 mb-[10px] mt-[10px] select-none'>
              Notes:
            </div>
            <SimpleTextarea
              onChange={value => setFormState(prev => ({ ...prev, inputOutputNotes: value }))}
              minRows={2}
              placeholder='Additional notes and instructions to help understand your needs better. Could be about some specific transformations you want, some example code snippets, etc.'
            />
          </div>
        </details>
      ) : (
        <div className='description'>
          <div className='label font-semibold text-xs text-default-700 mb-[10px] mt-[10px] select-none'>
            Notes (Optional):
          </div>
          <SimpleTextarea
            value={formState.inputOutputNotes}
            onChange={value => setFormState(prev => ({ ...prev, inputOutputNotes: value }))}
            minRows={2}
            placeholder='Additional notes and instructions to help understand your needs better. Could be about some specific transformations you want, some example code snippets, etc.'
            disabled={withExamplePrefilled}
          />
          {vmAware ? (
            <>
              <div className='label font-semibold text-xs text-default-700 mb-[10px] mt-[10px] flex justify-between items-center select-none'>
                <div className='flex items-center gap-2'>
                  <span>VM Args:</span>
                  {isLoadingSuggestedVmArgs && (
                    <div className='flex items-center gap-1 text-xs text-gray-500 select-none'>
                      <svg className='animate-spin h-3 w-3' viewBox='0 0 24 24'>
                        <circle
                          className='opacity-25'
                          cx='12'
                          cy='12'
                          r='10'
                          stroke='currentColor'
                          strokeWidth='4'
                          fill='none'
                        />
                        <path
                          className='opacity-75'
                          fill='currentColor'
                          d='m4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z'
                        />
                      </svg>
                      <span>Loading VM args suggestions from repository...</span>
                    </div>
                  )}
                </div>
                <InfoTooltip content='If running the test requires VM arguments, users can supply them to ensure the tests execute correctly within the appropriate environment settings.' />
              </div>
              <SimpleTextarea
                className='mx-0'
                value={formState.vmArgs}
                onChange={value => setFormState(prev => ({ ...prev, vmArgs: value }))}
                minRows={2}
                placeholder={suggestedVmArgs || ''}
                disabled={withExamplePrefilled}
              />
            </>
          ) : null}
        </div>
      )}
    </div>
  )
}
